import { createAuthClient } from 'better-auth/react';
import { inferAdditionalFields } from 'better-auth/client/plugins';

const getAuthBaseURL = () => {
  const apiBaseUrl = import.meta.env['VITE_API_BASE_URL'] as string | undefined;

  // If empty, use full localhost URL for Vite proxy
  // Better Auth requires absolute URLs
  if (!apiBaseUrl || apiBaseUrl === '') {
    return 'http://localhost:3000/auth';
  }

  // Otherwise use configured URL
  return `${apiBaseUrl}/auth`;
};

export const authClient = createAuthClient({
  baseURL: getAuthBaseURL(),
  plugins: [
    // Infer additional fields from backend schema
    // Since client and server are in separate projects, we manually specify fields
    // See: https://www.better-auth.com/docs/concepts/typescript
    inferAdditionalFields({
      user: {
        role: {
          type: 'string',
        },
        phoneNumber: {
          type: 'string',
        },
        status: {
          type: 'string',
        },
        phoneNumberVerified: {
          type: 'boolean',
        },
        hasCompletedOnboarding: {
          type: 'boolean',
        },
        veterinarianId: {
          type: 'string',
        },
      },
    }),
  ],
});

export const {
  signIn,
  signUp,
  signOut,
  useSession,
  resetPassword,
  sendVerificationEmail,
  verifyEmail,
  changePassword,
  updateUser,
} = authClient;

/**
 * Custom requestPasswordReset function that uses the correct API endpoint
 * The server uses /auth/request-password-reset instead of Better Auth's default /auth/forget-password
 */
export async function requestPasswordReset(data: { email: string; redirectTo?: string }) {
  const baseURL = getAuthBaseURL();
  const response = await fetch(`${baseURL}/request-password-reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      email: data.email,
      redirectTo: data.redirectTo,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { message?: string };
    return {
      error: {
        message: errorData.message || 'Erro ao solicitar redefinição de senha',
      },
      data: null,
    };
  }

  const responseData = await response.json() as { status: boolean; message: string };
  return { data: responseData, error: null };
}
